import {api} from "../api";
import {htmlEscape} from "../utils/string";
import {formatDate, getISODate, isSameDay, parseISODate, Temporal} from "@/shared/utils/datefunctions";
import {AbsenceTimeRange, PartialUser, PartialWorkingTimeType} from "@/shared/timr";
import {DurationUnit, WorkingTimeTypeCategory} from "@/shared/timrTypes";

const template = `
		<div class="panel panel-substitute warning mb-20" data-substitute-absence-warning-container>
			<div class="panel-body flex-row">
				<div class="flex-column justify-content-center mr-20">
					<i class="far fa-2xl fa-triangle-exclamation icon"></i>
				</div>
				<div class="flex-column" data-substitute-absence-warning></div>
			</div>
		</div>
		<div class="panel panel-substitute warning" data-already-substitute-container>
			<div class="panel-body flex-row">
				<div class="flex-column justify-content-center mr-20">
					<i class="far fa-2xl fa-triangle-exclamation icon"></i>
				</div>
				<div class="flex-column" data-already-substitute-text></div>
			</div>
		</div>
	`;

export class SubstituteValidation extends HTMLElement {

	public static readonly EVENT_START_LOADING = "substituteValidation.startLoading";
	public static readonly EVENT_FINISHED_LOADING = "substituteValidation.finishedLoading";

	private startDate: Temporal.PlainDate | null;
	private endDate: Temporal.PlainDate | null;
	private userId: number;
	private substitute?: PartialUser;
	private substitutionUserNames: string = "";
	private requiresSubstitute: boolean;
	private currentWorkingTimeType: PartialWorkingTimeType;

	private userCheckRequest: JQuery.jqXHR;
	private substituteCheckRequest: JQuery.jqXHR;

	private absenceWarningContainerElement: HTMLElement;
	private alreadySubstituteContainerElement: HTMLElement;
	private absenceWarningElement: HTMLElement;
	private alreadySubstituteTextElement: HTMLElement;

	public connectedCallback() {
		const substituteJSON = this.getAttribute("substitute");
		const substitute: PartialUser = substituteJSON ? JSON.parse(substituteJSON) as PartialUser : null;
		const substituteAbsencesJSON = this.getAttribute("substitute-absences");
		const substituteAbsences: AbsenceTimeRange[] = substituteAbsencesJSON ? JSON.parse(substituteAbsencesJSON) as AbsenceTimeRange[] : [];
		if (substitute && substituteAbsences) {
			this.init(true);
			this.substitute = substitute;
			this.buildSubstituteWarning(substituteAbsences);
		}
	}

	public init(validate: boolean) {
		this.innerHTML = template;
		this.requiresSubstitute = validate;
		this.absenceWarningContainerElement = this.querySelector("[data-substitute-absence-warning-container]");
		this.alreadySubstituteContainerElement = this.querySelector("[data-already-substitute-container]");
		this.absenceWarningElement = this.querySelector("[data-substitute-absence-warning]");
		this.alreadySubstituteTextElement = this.querySelector("[data-already-substitute-text]");
		this.hideAllWarnings();
	}

	/**
	 * Set the requires substitute flag and check if the user and substitute are available.
	 * If the flag is set to false, all warnings are hidden and all validations will not be performed.
	 *
	 * @param requiresSubstitute
	 */
	public setRequiresSubstitute(requiresSubstitute: boolean) {
		this.requiresSubstitute = requiresSubstitute;
		if (requiresSubstitute) {
			this.checkUserAvailability();
			this.checkSubstituteAvailability();
		} else {
			this.hideAllWarnings();
		}
	}

	public setWorkingTimeType(workingTimeType: PartialWorkingTimeType) {
		this.currentWorkingTimeType = workingTimeType;
		this.checkUserAvailability();
	}

	public validateUser(userId: number) {
		this.userId = userId;
		this.checkUserAvailability();
	}

	public validateSubstitute(substitute?: PartialUser) {
		this.substitute = substitute;
		this.checkSubstituteAvailability();
	}

	public setStartEndDate(startDate: Temporal.PlainDate, endDate: Temporal.PlainDate) {
		if (isSameDay(this.startDate, startDate) && isSameDay(this.endDate, endDate)) {
			return;
		} else {
			this.startDate = startDate;
			this.endDate = endDate;
			this.checkSubstituteAvailability();
			this.checkUserAvailability();
		}
	}

	public hasAlreadySubstitutions(): boolean {
		// Do not use this.alreadySubstituteContainerElement.checkVisibility(),
		// because it is not supported in all browsers.
		// Chrome >= 105, Firefox >= 106, Safari >= 17.4, Edge >= 105
		// Instead, check the display style directly.
		return this.alreadySubstituteContainerElement.style.display !== "none";
	}

	public getAlreadySubstitutionsUserNames(): string {
		return this.substitutionUserNames;
	}

	private hideAllWarnings() {
		this.toggle(this.absenceWarningContainerElement, false);
		this.toggle(this.alreadySubstituteContainerElement, false);
	}

	private checkUserAvailability() {
		this.userCheckRequest?.abort();
		if (this.isUserValidationRequired()) {
			this.fireEvent(SubstituteValidation.EVENT_START_LOADING);
			this.userCheckRequest = api("workingTimeDateSpans/checkUserAvailability.json")
				.get({
					userId: this.userId,
					from: getISODate(this.startDate),
					to: getISODate(this.endDate),
				})
				.done((absences: AbsenceTimeRange[]) => {
					this.substitutionUserNames = absences.map(atr => atr.userFullname).join(", ");
					this.alreadySubstituteTextElement.innerHTML = messages.withArguments("substituteSelection.alreadySubstitute", this.substitutionUserNames);
					this.toggle(this.alreadySubstituteContainerElement, absences.length > 0);
				})
				.always(() => this.fireFinishLoadingEvent());
		} else {
			this.toggle(this.alreadySubstituteContainerElement, false);
		}
	}

	private checkSubstituteAvailability() {
		const selectedSubstitute = this.substitute;
		if (this.requiresSubstitute && this.startDate && this.endDate && selectedSubstitute) {
			this.substituteCheckRequest?.abort();
			this.fireEvent(SubstituteValidation.EVENT_START_LOADING);
			this.substituteCheckRequest = api("workingTimeDateSpans/checkSubstituteAvailability.json")
				.get({
					userId: selectedSubstitute.id,
					from: getISODate(this.startDate),
					to: getISODate(this.endDate),
				})
				.done((absences: AbsenceTimeRange[]) => this.buildSubstituteWarning(absences))
				.always(() => this.fireFinishLoadingEvent());
		} else {
			this.toggle(this.absenceWarningContainerElement, false);
		}
	}

	private buildSubstituteWarning(absences: AbsenceTimeRange[]) {
		let message: string = "";
		const absenceList = document.createElement("ul");
		absenceList.classList.add("mb-0");
		const selectedSubstitute = this.substitute;
		const fullname = selectedSubstitute?.fullname;
		if (absences.length === 1) {
			const absence = absences[0];
			const start = parseISODate(absence.start);
			const end = parseISODate(absence.end);
			const absenceRequested = absence.isPending ? ".requested" : "";
			message = isSameDay(start, end)
				? messages.withArguments(`substituteSelection.substitute.absence.single.day${absenceRequested}`, fullname, formatDate(start))
				: messages.withArguments(`substituteSelection.substitute.absence.single.range${absenceRequested}`, fullname, formatDate(start), formatDate(end));
		} else if (absences.length > 1) {
			absenceList.append(...absences.map(absence => {
				const start = parseISODate(absence.start);
				const startFormatted = formatDate(start);
				const end = parseISODate(absence.end);
				const endFormatted = formatDate(end);
				const liElement = document.createElement("li");
				if (isSameDay(start, end)) {
					liElement.innerText = absence.isPending ? messages.withArguments("substituteSelection.substitute.absence.requested", startFormatted) : startFormatted;
				} else {
					liElement.innerText = absence.isPending ? messages.withArguments("substituteSelection.substitute.absence.requested", `${startFormatted} - ${endFormatted}`) : `${startFormatted} - ${endFormatted}`;
				}
				return liElement;
			}));
			message = messages.withArguments("substituteSelection.substitute.absence.multiple", fullname);
		}
		this.absenceWarningElement.innerHTML = htmlEscape(message);
		this.absenceWarningElement.append(absenceList);
		this.toggle(this.absenceWarningContainerElement, !!message && !!fullname && this.requiresSubstitute);
	}

	private isUserValidationRequired(): boolean {
		return this.startDate && this.endDate && this.userId &&
			this.currentWorkingTimeType?.editUnit !== DurationUnit.Minutes &&
			this.currentWorkingTimeType?.category !== WorkingTimeTypeCategory.AttendanceTime;
	}

	/**
	 * For now, we have to find a better way to handle the loading events.
	 */
	private fireFinishLoadingEvent() {
		void Promise.all([this.userCheckRequest, this.substituteCheckRequest])
			.then(() => this.fireEvent(SubstituteValidation.EVENT_FINISHED_LOADING))
			.catch(() => this.fireEvent(SubstituteValidation.EVENT_FINISHED_LOADING));
	}

	private fireEvent(eventName: string) {
		this.dispatchEvent(new CustomEvent(eventName));
	}

	private toggle(element: HTMLElement, showOrHide: boolean) {
		element.style.display = showOrHide ? 'block' : 'none';
	}
}
